import { List, OrderedSet } from 'immutable';
import { RFIAttachment } from 'records/RFIAttachment';
import { RFI } from 'records/RFI';
import { SheetVersion } from 'records/SheetVersion';
import { assign, DoneInvokeEvent, Machine } from 'xstate';
import {
  AppPages,
  AttachmentTypeEnum,
  CancelEvent,
  EmptyForwardMessage,
  EmptyRFIResponse,
  EmptyRFIRevision,
  ViewRFIContext,
  ViewRFIEvents,
  ViewRFIEventsEnum,
  ViewRFIMachineProps,
  ViewRFIStateMachine,
  ViewRFIStates,
  ViewRFIStatesEnum,
} from './viewRfiMachineTypes';

const createViewRfiMachine = ({
  requiredContext,
  services,
}: ViewRFIMachineProps): ViewRFIStateMachine =>
  Machine<ViewRFIContext, ViewRFIStates, ViewRFIEvents>(
    {
      id: 'viewRfiMachine',
      initial: ViewRFIStatesEnum.responding,
      context: {
        ...requiredContext,
        role: services.getUserRole(requiredContext),
        rfiAttachmentSheetVersions: List<SheetVersion>(),
        officialResponse: EmptyRFIResponse(),
        forwardList: OrderedSet<string>(),
        forwardMessage: EmptyForwardMessage(),
        forwardReply: EmptyRFIResponse(),
        selectedDiscussionIds: OrderedSet<string>(),
        rfiRevision: EmptyRFIRevision(),
        rfiVersionList: services.getRfiVersionList(requiredContext.rfi.id),
      },
      on: {
        [ViewRFIEventsEnum.DISCUSS]: ViewRFIStatesEnum.discussing,
        [ViewRFIEventsEnum.RESPOND]: ViewRFIStatesEnum.responding,
        [ViewRFIEventsEnum.CLOSE]: ViewRFIStatesEnum.done,
        [ViewRFIEventsEnum.RFI_SELECTED]: {
          target: ViewRFIStatesEnum.responding,
          actions: [
            assign({
              rfi: (_, { data }) => data,
            }),
            'resetContext',
          ],
        },
      },
      states: {
        [ViewRFIStatesEnum.responding]: {
          entry: [
            ({ rfi }) => services.getSheetVersionsFromPageIds(rfi.pageids),
            'getOfficialResponseSheetVersions',
            'getForwardActivitySheetVersions',
            'getDiscussionUsers',
          ],
          always: [
            {
              target: ViewRFIStatesEnum.closed,
              cond: 'hasAnswer',
            },
            {
              target: ViewRFIStatesEnum.creatingRfiVersion,
              cond: 'isCreating',
            },
            {
              target: ViewRFIStatesEnum.creatingRfiDraft,
              cond: 'isDraft',
            },
            {
              target: ViewRFIStatesEnum.reopened,
              cond: 'isReopened',
            },
            {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
              cond: 'notAnswered',
            },
          ],
        },
        [ViewRFIStatesEnum.reopened]: {
          always: [
            {
              target: ViewRFIStatesEnum.reopenedRfiOnViewerOrMapPage,
              cond: 'isViewerOrMapPage',
            },
            {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
            },
          ],
        },
        [ViewRFIStatesEnum.pendingOfficialResponse]: {
          on: {
            [ViewRFIEventsEnum.RESPOND]:
              ViewRFIStatesEnum.creatingOfficialResponse,
            [ViewRFIEventsEnum.DISCUSS]: ViewRFIStatesEnum.discussing,
          },
        },
        [ViewRFIStatesEnum.creatingOfficialResponse]: {
          on: {
            [ViewRFIEventsEnum.SEND]: ViewRFIStatesEnum.sendingOfficialResponse,
            [ViewRFIEventsEnum.CANCEL]: {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
              actions: 'resetContext',
            },
            [ViewRFIEventsEnum.CANCEL_WITH_DISCUSSION_ATTACHMENT]: {
              target: ViewRFIStatesEnum.cancellingWithDiscussionAttachment,
              actions: [
                assign({
                  selectedDiscussionIds: (_, event) =>
                    OrderedSet<string>([event.data.id]),
                }),
                'resetContext',
              ],
            },
            [ViewRFIEventsEnum.UPDATE]: {
              actions: 'updateOfficialResponse',
            },
            [ViewRFIEventsEnum.UPDATE_ATTACHMENT]: {
              actions: 'updateOfficialResponseAttachment',
            },
            [ViewRFIEventsEnum.REMOVE_ATTACHMENT]: {
              actions: 'removeOfficialResponseAttachment',
            },
            [ViewRFIEventsEnum.DELETE_DISCUSSION_ATTACHMENT]: {
              target: ViewRFIStatesEnum.deletingAttachedDiscussion,
              actions: assign({
                selectedDiscussionIds: (_, event) =>
                  OrderedSet<string>([event.data.id]),
              }),
            },
            [ViewRFIEventsEnum.VIEW_ATTACHED_DISCUSSION]:
              ViewRFIStatesEnum.viewingAttachedDiscussions,
          },
        },
        [ViewRFIStatesEnum.viewingAttachedDiscussions]: {
          on: {
            [ViewRFIEventsEnum.CANCEL]: {
              target: ViewRFIStatesEnum.creatingOfficialResponse,
              actions: assign<ViewRFIContext, CancelEvent>({
                selectedDiscussionIds: OrderedSet<string>(),
              }),
            },
            [ViewRFIEventsEnum.UPDATE_SELECTED_DISCUSSIONS]: {
              actions: assign({
                selectedDiscussionIds: (_, event) => event.data.ids,
              }),
            },
            [ViewRFIEventsEnum.ATTACH_DISCUSSION]:
              ViewRFIStatesEnum.attachingDiscussionToResponse,
          },
        },
        [ViewRFIStatesEnum.sendingOfficialResponse]: {
          invoke: {
            id: 'sendingOfficialResponse',
            src: (context) => services.sendOfficialResponse(context),
            onDone: {
              target: ViewRFIStatesEnum.closed,
              actions: [
                assign({
                  rfi: (_, event) => event.data,
                }),
                'getDiscussionUsers',
                'getOfficialResponseSheetVersions',
                'getForwardActivitySheetVersions',
                'resetContext',
              ],
            },
            onError: ViewRFIStatesEnum.creatingOfficialResponse,
          },
        },
        [ViewRFIStatesEnum.discussing]: {
          always: [
            {
              target: ViewRFIStatesEnum.closed,
              cond: 'hasAnswer',
            },
            {
              target: ViewRFIStatesEnum.discussionAttached,
              cond: 'hasAttachedDiscussion',
            },
            {
              target: ViewRFIStatesEnum.pendingForwardReply,
              cond: 'hasBeenForwarded',
            },
            {
              target: ViewRFIStatesEnum.pendingDiscussionAttachment,
              cond: 'hasForwardReply',
            },
            {
              target: ViewRFIStatesEnum.pendingForward,
              cond: 'hasNoDiscussion',
            },
          ],
        },
        [ViewRFIStatesEnum.pendingForward]: {
          on: {
            [ViewRFIEventsEnum.FORWARD]:
              ViewRFIStatesEnum.creatingForwardMessage,
            [ViewRFIEventsEnum.RESPOND]: ViewRFIStatesEnum.responding,
          },
        },
        [ViewRFIStatesEnum.creatingForwardMessage]: {
          on: {
            [ViewRFIEventsEnum.CANCEL]: {
              target: ViewRFIStatesEnum.pendingForward,
              actions: 'resetContext',
            },
            [ViewRFIEventsEnum.SEND]: ViewRFIStatesEnum.sendingForwardMessage,
            [ViewRFIEventsEnum.RESPOND]: ViewRFIStatesEnum.responding,
            [ViewRFIEventsEnum.UPDATE]: {
              actions: 'updateForwardMessage',
            },
            [ViewRFIEventsEnum.UPDATE_FORWARD_LIST]: {
              actions: 'updateForwardList',
            },
            [ViewRFIEventsEnum.UPDATE_ATTACHMENT]: {
              actions: 'updateForwardAttachment',
            },
            [ViewRFIEventsEnum.REMOVE_ATTACHMENT]: {
              actions: 'removeForwardAttachment',
            },
          },
        },
        [ViewRFIStatesEnum.sendingForwardMessage]: {
          invoke: {
            id: 'sendingForwardMessage',
            src: (context) => services.sendForwardMessage(context),
            onDone: {
              target: ViewRFIStatesEnum.pendingForwardReply,
              actions: [
                assign({
                  rfi: (_, event) => event.data,
                }),
                'getDiscussionUsers',
                'getForwardActivitySheetVersions',
                'resetContext',
              ],
            },
            onError: ViewRFIStatesEnum.creatingForwardMessage,
          },
        },
        [ViewRFIStatesEnum.pendingForwardReply]: {
          on: {
            [ViewRFIEventsEnum.CANCEL]: ViewRFIStatesEnum.responding,
            [ViewRFIEventsEnum.RESPOND]: ViewRFIStatesEnum.responding,
            [ViewRFIEventsEnum.REPLY]: ViewRFIStatesEnum.creatingForwardReply,
          },
        },
        [ViewRFIStatesEnum.creatingForwardReply]: {
          on: {
            [ViewRFIEventsEnum.SEND]: ViewRFIStatesEnum.sendingForwardReply,
            [ViewRFIEventsEnum.CANCEL]: ViewRFIStatesEnum.pendingForwardReply,
            [ViewRFIEventsEnum.UPDATE]: {
              actions: 'updateForwardReply',
            },
            [ViewRFIEventsEnum.UPDATE_ATTACHMENT]: {
              actions: 'updateForwardReplyAttachment',
            },
            [ViewRFIEventsEnum.REMOVE_ATTACHMENT]: {
              actions: 'removeForwardReplyAttachment',
            },
          },
        },
        [ViewRFIStatesEnum.sendingForwardReply]: {
          invoke: {
            id: 'sendingForwardReply',
            src: (context) => services.sendForwardReply(context),
            onDone: {
              target: ViewRFIStatesEnum.pendingDiscussionAttachment,
              actions: [
                assign({
                  rfi: (_, event) => event.data,
                }),
                'getDiscussionUsers',
                'getForwardActivitySheetVersions',
                'resetContext',
              ],
            },
            onError: ViewRFIStatesEnum.creatingForwardReply,
          },
        },
        [ViewRFIStatesEnum.pendingDiscussionAttachment]: {
          on: {
            [ViewRFIEventsEnum.CANCEL]: ViewRFIStatesEnum.responding,
            [ViewRFIEventsEnum.ATTACH_DISCUSSION]:
              ViewRFIStatesEnum.attachingDiscussionToResponse,
            [ViewRFIEventsEnum.UPDATE_SELECTED_DISCUSSIONS]: {
              actions: assign({
                selectedDiscussionIds: (_, event) => event.data.ids,
              }),
            },
          },
        },
        [ViewRFIStatesEnum.attachingDiscussionToResponse]: {
          invoke: {
            id: 'attachingDiscussionToResponse',
            src: (context) => services.attachDiscussionToResponse(context),
            onDone: {
              target: ViewRFIStatesEnum.creatingOfficialResponse,
              actions: [
                assign<ViewRFIContext, DoneInvokeEvent<RFI>>({
                  rfi: (_, event) => event.data,
                  selectedDiscussionIds: OrderedSet<string>(),
                }),
                'getDiscussionUsers',
              ],
            },
            onError: {
              target: ViewRFIStatesEnum.pendingDiscussionAttachment,
              actions: (err) => console.log(err),
            },
          },
        },
        [ViewRFIStatesEnum.cancellingWithDiscussionAttachment]: {
          invoke: {
            id: 'cancellingWithDiscussionAttachment',
            src: (context) => services.deleteAttachedDiscussion(context),
            onDone: {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
              actions: [
                assign<ViewRFIContext, DoneInvokeEvent<RFI>>({
                  rfi: (_, event) => event.data,
                  selectedDiscussionIds: OrderedSet<string>(),
                }),
                'getDiscussionUsers',
              ],
            },
            onError: {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
              actions: (err) => console.log(err),
            },
          },
        },
        [ViewRFIStatesEnum.deletingAttachedDiscussion]: {
          invoke: {
            id: 'deletingDiscussionResponse',
            src: (context) => services.deleteAttachedDiscussion(context),
            onDone: {
              target: ViewRFIStatesEnum.creatingOfficialResponse,
              actions: [
                assign<ViewRFIContext, DoneInvokeEvent<RFI>>({
                  rfi: (_, event) => event.data,
                  selectedDiscussionIds: OrderedSet<string>(),
                }),
                'getDiscussionUsers',
              ],
            },
            onError: {
              target: ViewRFIStatesEnum.creatingOfficialResponse,
              actions: (err) => console.log(err),
            },
          },
        },
        [ViewRFIStatesEnum.discussionAttached]: {},
        [ViewRFIStatesEnum.reopeningRfi]: {
          invoke: {
            id: 'reopeningRfi',
            src: (context) => services.reopenRfi(context),
            onDone: {
              target: ViewRFIStatesEnum.pendingOfficialResponse,
              actions: [
                assign<ViewRFIContext, DoneInvokeEvent<RFI>>({
                  rfi: (_, event) => event.data,
                }),
                'resetContext',
              ],
            },
          },
        },
        [ViewRFIStatesEnum.creatingRfiVersion]: {
          entry: assign({
            rfiRevision: ({ rfi, currentUser, rfiRevision }) => ({
              ...rfiRevision,
              rfi: new RFI({
                title: rfi.title,
                projectid: rfi.projectid,
                previousversionid: rfi.id,
                number: rfi.number,
                versionnumber: rfi.versionnumber + 1,
                duedate: Math.floor(
                  new Date().setDate(new Date().getDate() + 3) / 1000
                ),
                submittingname: `${currentUser?.profile.firstname} ${currentUser?.profile.lastname}`,
                submittingemail: currentUser?.email,
                submittingcompany: currentUser?.profile.company || ' ',
              }),
            }),
          }),
          on: {
            [ViewRFIEventsEnum.SEND]: {
              target: ViewRFIStatesEnum.reopeningRfi,
            },
            [ViewRFIEventsEnum.SAVE]: {
              target: ViewRFIStatesEnum.savingRfiDraft,
            },
            [ViewRFIEventsEnum.UPDATE_RFI]: {
              actions: assign({
                rfiRevision: ({ rfiRevision }, { data }) => ({
                  ...rfiRevision,
                  rfi: data,
                }),
              }),
            },
            [ViewRFIEventsEnum.UPDATE_ATTACHMENT]: {
              actions: assign({
                rfiRevision: ({ rfiRevision, project }, event) => ({
                  ...rfiRevision,
                  attachments: services.updateAttachments(
                    event,
                    rfiRevision.attachments,
                    project.id
                  ),
                }),
              }),
            },
            [ViewRFIEventsEnum.REMOVE_ATTACHMENT]: {
              actions: assign({
                rfiRevision: ({ rfiRevision }, event) => ({
                  ...rfiRevision,
                  attachments: services.removeAttachments(
                    event,
                    rfiRevision.attachments
                  ),
                }),
              }),
            },
            [ViewRFIEventsEnum.CANCEL]: {
              target: ViewRFIStatesEnum.responding,
              actions: assign<ViewRFIContext, CancelEvent>({
                rfiRevision: () => EmptyRFIRevision(),
              }),
            },
          },
        },
        [ViewRFIStatesEnum.creatingRfiDraft]: {
          entry: assign({
            rfiRevision: ({ rfiRevision, rfi }) => ({
              ...rfiRevision,
              rfi,
              attachments: {
                ...rfiRevision.attachments,
                [AttachmentTypeEnum.DRAWING]: OrderedSet<string>(rfi.pageids),
                [AttachmentTypeEnum.SAVED]: rfi.attachments,
                drawingSheetVersions: services.getSheetVersionsFromPageIds(
                  rfi.pageids
                ),
              },
            }),
          }),
          on: {
            [ViewRFIEventsEnum.SEND]: {
              target: ViewRFIStatesEnum.publishingRfiDraft,
            },
            [ViewRFIEventsEnum.SAVE]: ViewRFIStatesEnum.updatingRfiDraft,
            [ViewRFIEventsEnum.DELETE_CONFIRM]: {
              target: ViewRFIStatesEnum.deletingRfiDraftConfirm,
            },
            [ViewRFIEventsEnum.DELETE]: {
              target: ViewRFIStatesEnum.deletingRfiDraft,
            },
            [ViewRFIEventsEnum.UPDATE_RFI]: {
              actions: assign({
                rfiRevision: ({ rfiRevision }, { data }) => ({
                  ...rfiRevision,
                  rfi: data,
                }),
              }),
            },
            [ViewRFIEventsEnum.UPDATE_ATTACHMENT]: {
              actions: assign({
                rfiRevision: ({ rfiRevision, project }, event) => ({
                  ...rfiRevision,
                  attachments: services.updateAttachments(
                    event,
                    rfiRevision.attachments,
                    project.id
                  ),
                }),
              }),
            },
            [ViewRFIEventsEnum.REMOVE_ATTACHMENT]: {
              actions: assign({
                rfiRevision: ({ rfiRevision }, event) => ({
                  ...rfiRevision,
                  attachments: services.removeAttachments(
                    event,
                    rfiRevision.attachments
                  ),
                  attachmentsToRemove:
                    event.data.source === AttachmentTypeEnum.SAVED
                      ? rfiRevision.attachmentsToRemove.push(
                          event.data.item as RFIAttachment
                        )
                      : rfiRevision.attachmentsToRemove,
                }),
              }),
            },
          },
        },
        [ViewRFIStatesEnum.publishingRfiDraft]: {
          invoke: {
            id: 'publishingRfiDraft',
            src: (context) => services.publishDraft(context),
            onDone: {
              target: ViewRFIStatesEnum.responding,
              actions: assign<ViewRFIContext, DoneInvokeEvent<RFI>>({
                rfi: (_, event) => event.data,
              }),
            },
          },
        },
        [ViewRFIStatesEnum.updatingRfiDraft]: {
          invoke: {
            id: 'updatingRfiDraft',
            src: (context) => services.updateDraft(context),
            onDone: {
              target: ViewRFIStatesEnum.done,
            },
          },
        },
        [ViewRFIStatesEnum.deletingRfiDraftConfirm]: {
          on: {
            [ViewRFIEventsEnum.DELETE]: {
              target: ViewRFIStatesEnum.deletingRfiDraft,
            },
            [ViewRFIEventsEnum.CANCEL]: {
              target: ViewRFIStatesEnum.creatingRfiDraft,
            },
          },
        },
        [ViewRFIStatesEnum.deletingRfiDraft]: {
          invoke: {
            id: 'deletingRfiDraft',
            src: (context) => services.deleteDraft(context),
            onDone: {
              target: ViewRFIStatesEnum.done,
            },
          },
        },
        [ViewRFIStatesEnum.savingRfiDraft]: {
          invoke: {
            id: 'savingDraft',
            src: (context) => services.saveDraft(context),
            onDone: {
              target: ViewRFIStatesEnum.done,
            },
          },
        },
        [ViewRFIStatesEnum.reopenedRfiOnViewerOrMapPage]: {},
        [ViewRFIStatesEnum.closed]: {
          on: {
            [ViewRFIEventsEnum.REOPEN_RFI]: {
              target: ViewRFIStatesEnum.creatingRfiVersion,
            },
          },
        },
        [ViewRFIStatesEnum.done]: {
          type: 'final',
        },
      },
    },
    {
      actions: {
        resetContext: assign((context) => {
          return {
            ...context,
            rfiVersionList: services.getRfiVersionList(context.rfi.id),
            role: services.getUserRole(context),
            officialResponse: EmptyRFIResponse(),
            forwardReply: EmptyRFIResponse(),
            forwardMessage: EmptyForwardMessage(),
          };
        }),
        updateForwardMessage: assign({
          forwardMessage: ({ forwardMessage }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE) return forwardMessage;
            return {
              ...forwardMessage,
              message: event.data.value,
            };
          },
        }),
        updateForwardList: assign({
          forwardMessage: ({ forwardMessage }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE_FORWARD_LIST)
              return forwardMessage;
            return {
              ...forwardMessage,
              forwardList: event.data.forwardList,
            };
          },
        }),
        updateForwardAttachment: assign({
          forwardMessage: ({ forwardMessage, project }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE_ATTACHMENT)
              return forwardMessage;

            return {
              ...forwardMessage,
              attachments: services.updateAttachments(
                event,
                forwardMessage.attachments,
                project.id
              ),
            };
          },
        }),
        removeForwardAttachment: assign({
          forwardMessage: ({ forwardMessage }, event) => {
            if (event.type !== ViewRFIEventsEnum.REMOVE_ATTACHMENT)
              return forwardMessage;

            return {
              ...forwardMessage,
              attachments: services.removeAttachments(
                event,
                forwardMessage.attachments
              ),
            };
          },
        }),
        updateOfficialResponse: assign({
          officialResponse: ({ officialResponse }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE) {
              return officialResponse;
            }
            return { ...officialResponse, message: event.data.value };
          },
        }),
        updateOfficialResponseAttachment: assign({
          officialResponse: ({ officialResponse, project }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE_ATTACHMENT) {
              return officialResponse;
            }

            return {
              ...officialResponse,
              attachments: services.updateAttachments(
                event,
                officialResponse.attachments,
                project.id
              ),
            };
          },
        }),
        removeOfficialResponseAttachment: assign({
          officialResponse: ({ officialResponse }, event) => {
            if (event.type !== ViewRFIEventsEnum.REMOVE_ATTACHMENT)
              return officialResponse;

            return {
              ...officialResponse,
              attachments: services.removeAttachments(
                event,
                officialResponse.attachments
              ),
            };
          },
        }),
        updateForwardReply: assign({
          forwardReply: ({ forwardReply }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE) {
              return forwardReply;
            }
            return { ...forwardReply, message: event.data.value };
          },
        }),
        updateForwardReplyAttachment: assign({
          forwardReply: ({ forwardReply, project }, event) => {
            if (event.type !== ViewRFIEventsEnum.UPDATE_ATTACHMENT) {
              return forwardReply;
            }

            return {
              ...forwardReply,
              attachments: services.updateAttachments(
                event,
                forwardReply.attachments,
                project.id
              ),
            };
          },
        }),
        removeForwardReplyAttachment: assign({
          forwardReply: ({ forwardReply }, event) => {
            if (event.type !== ViewRFIEventsEnum.REMOVE_ATTACHMENT)
              return forwardReply;

            return {
              ...forwardReply,
              attachments: services.removeAttachments(
                event,
                forwardReply.attachments
              ),
            };
          },
        }),
        getForwardActivitySheetVersions: assign({
          rfi: ({ rfi }) => {
            if (rfi.forwardactivity.size > 0) {
              const activities = rfi.forwardactivity.map((activity) => {
                if (activity.pageids && activity.pageids.size > 0) {
                  return activity.set(
                    'sheetVersions',
                    services.getSheetVersionsFromPageIds(activity.pageids)
                  );
                }
                return activity;
              });

              return rfi.set('forwardactivity', activities);
            }
            return rfi;
          },
          rfiAttachmentSheetVersions: ({ rfi }) =>
            services.getSheetVersionsFromPageIds(rfi.pageids),
        }),
        getOfficialResponseSheetVersions: assign({
          rfi: ({ rfi }) => {
            if (rfi.answer?.pageids && rfi.answer?.pageids.size > 0) {
              const sheetversions = services.getSheetVersionsFromPageIds(
                rfi.answer?.pageids
              );

              return rfi.setIn(['answer', 'sheetVersions'], sheetversions);
            }

            return rfi;
          },
        }),
        getDiscussionUsers: assign({
          rfi: ({ rfi }) => {
            if (rfi.forwardactivity.size > 0) {
              const activities = rfi.forwardactivity.map((a) =>
                a.set('user', services.getUserByEmail(a.email))
              );
              return rfi.set('forwardactivity', activities);
            }
            return rfi;
          },
        }),
      },
      guards: {
        hasForwardReply: ({ rfi }) => rfi.forwardactivity.size > 1,
        hasBeenForwarded: ({ rfi }) => rfi.forwardactivity.size === 1,
        hasNoDiscussion: ({ rfi }) => rfi.forwardactivity.size === 0,
        hasAnswer: ({ rfi }: ViewRFIContext) => !!rfi?.answer,
        notAnswered: ({ rfi }: ViewRFIContext) => !rfi?.answer,
        isCreating: ({ rfi }: ViewRFIContext) => rfi.id === '',
        isDraft: ({ rfi }: ViewRFIContext) => rfi.craeteddate === 0,
        hasAttachedDiscussion: ({ rfi }: ViewRFIContext) =>
          rfi?.discussionattachmentids.size > 0,
        isReopened: ({ rfi }: ViewRFIContext) => rfi.versionnumber !== 0,
        isViewerOrMapPage: ({ appPage }: ViewRFIContext) =>
          appPage === 'VIEWER' || appPage === 'MAP',
      },
    }
  );

export { createViewRfiMachine };
